import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { navLinks, solutionsDropdown } from '../data/mock';
import { prefetchRoute } from '../lib/route_prefetch';
import { ChevronDown, Menu, X, LayoutDashboard, LogIn } from 'lucide-react';
import { getUpcomingEventsCount, hasEventThisWeek } from '../config/events';
import { useAuth } from '../contexts/AuthContext';
import SubscriptionPill from './SubscriptionPill';

const Header = () => {
  const upcomingEventsCount = getUpcomingEventsCount();
  const eventThisWeek = hasEventThisWeek();
  const shouldPulse = upcomingEventsCount >= 2 || eventThisWeek;
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNavigating, setIsNavigating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();

  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Smooth scroll to element with offset for header
  const smoothScrollTo = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      const headerOffset = 100;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Handle navigation to sections with smooth transition
  const handleSectionClick = async (e, href) => {
    e.preventDefault();
    const sectionId = href.replace('#', '');
    
    if (isHomePage) {
      // If on homepage, just scroll smoothly to section
      smoothScrollTo(sectionId);
    } else {
      // If on another page, fade out, navigate, then scroll
      setIsNavigating(true);
      
      // Add fade-out class to body
      document.body.classList.add('page-transitioning');
      
      // Wait for fade out animation
      await new Promise(resolve => setTimeout(resolve, 250));
      
      // Navigate to homepage
      navigate('/');
      
      // Small delay for route change
      await new Promise(resolve => setTimeout(resolve, 50));
      
      // Remove fade class
      document.body.classList.remove('page-transitioning');
      
      // Scroll to section after page renders
      setTimeout(() => {
        smoothScrollTo(sectionId);
        setIsNavigating(false);
      }, 150);
    }
    
    setIsMobileMenuOpen(false);
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-400 ease-in-out ${
        isScrolled || !isHomePage ? 'bg-[#FDF8FA]/95 backdrop-blur-sm border-b border-[#C5366A]/10 shadow-sm' : 'bg-transparent'
      }`}
      style={{ height: '80px' }}
    >
      <div className="max-w-[1400px] mx-auto px-[7.6923%] h-full flex items-center justify-between">
        {/* Logo */}
        <a href="/" className="flex items-center">
          <img
            src="https://customer-assets.emergentagent.com/job_3a5ab85f-da0a-4b5c-ba39-99c6e6da22ed/artifacts/l8m2yxt7_logo%20big%20razblizovsky.png"
            alt="Logo"
            className="h-10 object-contain"
          />
        </a>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <div key={link.label} className="relative">
              {link.hasDropdown ? (
                <button
                  className="flex items-center gap-1 text-[#2D2D2D] hover:text-[#C5366A] transition-colors duration-300 text-lg font-normal"
                  onMouseEnter={() => setIsDropdownOpen(true)}
                  onMouseLeave={() => setIsDropdownOpen(false)}
                >
                  {link.label}
                  <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${isDropdownOpen ? 'rotate-180' : ''}`} />
                </button>
              ) : link.href.startsWith('#') ? (
                <a
                  href={link.href}
                  onClick={(e) => handleSectionClick(e, link.href)}
                  className="text-[#2D2D2D] hover:text-[#C5366A] transition-colors duration-300 text-lg font-normal"
                >
                  {link.label}
                </a>
              ) : (
                <a
                  href={link.href}
                  onMouseEnter={() => prefetchRoute.fetch(link.href)}
                  onTouchStart={() => prefetchRoute.fetch(link.href)}
                  className="text-[#2D2D2D] hover:text-[#C5366A] transition-colors duration-300 text-lg font-normal"
                >
                  {link.label}
                </a>
              )}

              {/* Dropdown */}
              {link.hasDropdown && (
                <div
                  className={`absolute top-full right-0 mt-2 min-w-[280px] bg-white border border-[#C5366A]/10 rounded-lg shadow-lg transition-all duration-300 ${
                    isDropdownOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'
                  }`}
                  onMouseEnter={() => setIsDropdownOpen(true)}
                  onMouseLeave={() => setIsDropdownOpen(false)}
                >
                  {solutionsDropdown.map((item) => (
                    <a
                      key={item.label}
                      href={item.href}
                      onMouseEnter={() => prefetchRoute.fetch(item.href)}
                      onTouchStart={() => prefetchRoute.fetch(item.href)}
                      className="block px-6 py-4 text-[#2D2D2D] hover:text-[#C5366A] hover:bg-[#F8EEF2] transition-all duration-300 text-base first:rounded-t-lg last:rounded-b-lg"
                    >
                      {item.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Members-area link — only for logged-in users */}
          {authUser && (
            <a
              href="/members"
              className="flex items-center gap-1.5 text-[#C5366A] hover:text-[#A12D56] transition-colors text-base font-bold border border-[#C5366A]/30 hover:border-[#C5366A] px-3 py-1.5 rounded-lg"
              onMouseEnter={() => prefetchRoute.fetch('/members')}
              data-testid="header-members-link"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span>אזור החברים</span>
            </a>
          )}

          {/* Login button — for non-authenticated visitors, always visible */}
          {!authUser && (
            <a
              href="/login"
              className="flex items-center gap-1.5 text-[#2D2D2D] hover:text-[#C5366A] transition-colors text-base font-bold border border-[#C5366A]/30 hover:border-[#C5366A] hover:bg-[#F8EEF2] px-4 py-1.5 rounded-lg"
              onMouseEnter={() => prefetchRoute.fetch('/login')}
              data-testid="header-login-btn"
            >
              <LogIn className="w-4 h-4" />
              <span>התחברות</span>
            </a>
          )}

          {/* CTA Button - Upcoming Events */}
          <a
            href="/events"
            className="flex items-center gap-2 bg-[#C5366A] text-white px-6 py-3.5 text-lg font-medium rounded-lg hover:bg-[#A12D56] transition-all duration-400 ease-in-out shadow-md"
            data-testid="header-events-cta"
          >
            <span>אירועים קרובים</span>
            {upcomingEventsCount > 0 && (
              <span
                className={`inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-white text-[#C5366A] text-sm font-extrabold rounded-full leading-none ${shouldPulse ? 'badge-pulse' : ''}`}
                aria-label={`${upcomingEventsCount} אירועים קרובים`}
                data-testid="header-events-count"
              >
                {upcomingEventsCount}
              </span>
            )}
            {eventThisWeek && (
              <span
                className="hidden lg:inline text-xs font-bold text-white/95 bg-white/15 px-2 py-0.5 rounded-full leading-none"
                data-testid="header-events-this-week"
              >
                השבוע!
              </span>
            )}
          </a>
        </nav>

        {/* Mobile: subscription pill + avatar (logged-in only) OR login button + menu */}
        <div className="md:hidden flex items-center gap-2 flex-1 justify-end min-w-0">
          {authUser ? (
            <>
              <SubscriptionPill authUser={authUser} testid="header-mobile-pill" />
              {authUser.picture ? (
                <img
                  src={authUser.picture}
                  alt=""
                  className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                  data-testid="header-mobile-avatar"
                />
              ) : (
                <a
                  href="/members"
                  className="w-8 h-8 rounded-full bg-[#F8EEF2] text-[#C5366A] font-bold flex items-center justify-center text-sm flex-shrink-0"
                  data-testid="header-mobile-avatar"
                >
                  {(authUser.name || authUser.email || '?').charAt(0).toUpperCase()}
                </a>
              )}
            </>
          ) : (
            <a
              href="/login"
              className="flex items-center gap-1 text-[#C5366A] hover:bg-[#F8EEF2] border border-[#C5366A]/30 px-2.5 py-1.5 rounded-lg text-sm font-bold flex-shrink-0"
              onMouseEnter={() => prefetchRoute.fetch('/login')}
              data-testid="header-login-btn-mobile"
            >
              <LogIn className="w-3.5 h-3.5" />
              <span>כניסה</span>
            </a>
          )}
          <button
            className="text-[#2D2D2D] p-2 flex-shrink-0"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label="פתח תפריט"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden absolute top-full left-0 right-0 bg-white border-t border-[#C5366A]/10 shadow-lg transition-all duration-300 ${
          isMobileMenuOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
      >
        <nav className="px-5 py-6 space-y-4">
          {navLinks.map((link) => (
            <div key={link.label}>
              {link.hasDropdown ? (
                <div className="space-y-3">
                  <span className="block text-[#2D2D2D] text-lg font-medium">{link.label}</span>
                  <div className="pr-4 space-y-3">
                    {solutionsDropdown.map((item) => (
                      <a
                        key={item.label}
                        href={item.href}
                        className="block text-[#6B6B6B] hover:text-[#C5366A] transition-colors text-base"
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {item.label}
                      </a>
                    ))}
                  </div>
                </div>
              ) : (
                <a
                  href={link.href}
                  onClick={(e) => link.href.startsWith('#') && handleSectionClick(e, link.href)}
                  className="block text-[#2D2D2D] text-lg hover:text-[#C5366A] transition-colors"
                >
                  {link.label}
                </a>
              )}
            </div>
          ))}
          {authUser && (
            <a
              href="/members"
              className="flex items-center justify-center gap-2 text-[#C5366A] font-bold text-base border-2 border-[#C5366A]/40 hover:bg-[#C5366A]/5 px-6 py-3 rounded-lg mt-4"
              onClick={() => setIsMobileMenuOpen(false)}
              data-testid="header-members-link-mobile"
            >
              <LayoutDashboard className="w-4 h-4" />
              <span>אזור החברים</span>
            </a>
          )}
          <a
            href="/events"
            className="flex items-center justify-center gap-2 bg-[#C5366A] text-white px-6 py-3.5 text-lg font-medium text-center mt-6 rounded-lg"
            data-testid="header-events-cta-mobile"
          >
            <span>אירועים קרובים</span>
            {upcomingEventsCount > 0 && (
              <span
                className={`inline-flex items-center justify-center min-w-[1.5rem] h-6 px-1.5 bg-white text-[#C5366A] text-sm font-extrabold rounded-full leading-none ${shouldPulse ? 'badge-pulse' : ''}`}
                aria-label={`${upcomingEventsCount} אירועים קרובים`}
              >
                {upcomingEventsCount}
              </span>
            )}
            {eventThisWeek && (
              <span className="text-xs font-bold text-white/95 bg-white/15 px-2 py-0.5 rounded-full leading-none">השבוע!</span>
            )}
          </a>
        </nav>
      </div>
    </header>
  );
};

export default Header;
